/**
 * 실측 화재시험 시료 레지스트리 (Sprint 22 #12 — Step 3)
 *
 * 총 20 samples: Cotton D1~D5, Nylon D6~D10, Polyester D11~D15, Linen D16~D20
 * D19: MS 결측 (TIC only) — msRaw = null
 *
 * Vite ?raw eager import + lazy parse cache
 * Bundle size 추정: TIC ~700KB raw, gzip ~150KB
 */

import type { MassSpectrumData, TICData } from "@/types/spectrum";
import { d1CottonTIC, d1CottonMSScans } from "./parseD1Cotton";
import { d6NylonTIC, d6NylonMSScans } from "./parseD6Nylon";
import {
  parseTICCsv,
  parseMSScans,
  getNearestScan,
  type MSScan,
} from "./parseRealSample";

// ─── Vite ?raw eager imports ──────────────────────────────────────────────────

// Cotton D2~D5 (TIC: csv, MS: csv 단 D3만 txt)
import d2CottonTICRaw from "@/assets/real-data/d2-cotton-tic.csv?raw";
import d2CottonMSRaw from "@/assets/real-data/d2-cotton-ms.csv?raw";
import d3CottonTICRaw from "@/assets/real-data/d3-cotton-tic.csv?raw";
import d3CottonMSRaw from "@/assets/real-data/d3-cotton-ms.txt?raw";
import d4CottonTICRaw from "@/assets/real-data/d4-cotton-tic.csv?raw";
import d4CottonMSRaw from "@/assets/real-data/d4-cotton-ms.csv?raw";
import d5CottonTICRaw from "@/assets/real-data/d5-cotton-tic.csv?raw";
import d5CottonMSRaw from "@/assets/real-data/d5-cotton-ms.csv?raw";

// Nylon D7~D10 (TIC+MS: txt tab)
import d7NylonTICRaw from "@/assets/real-data/d7-nylon-tic.txt?raw";
import d7NylonMSRaw from "@/assets/real-data/d7-nylon-ms.txt?raw";
import d8NylonTICRaw from "@/assets/real-data/d8-nylon-tic.txt?raw";
import d8NylonMSRaw from "@/assets/real-data/d8-nylon-ms.txt?raw";
import d9NylonTICRaw from "@/assets/real-data/d9-nylon-tic.txt?raw";
import d9NylonMSRaw from "@/assets/real-data/d9-nylon-ms.txt?raw";
import d10NylonTICRaw from "@/assets/real-data/d10-nylon-tic.txt?raw";
import d10NylonMSRaw from "@/assets/real-data/d10-nylon-ms.txt?raw";

// Polyester D11~D15 (TIC+MS: txt tab)
import d11PolyTICRaw from "@/assets/real-data/d11-polyester-tic.txt?raw";
import d11PolyMSRaw from "@/assets/real-data/d11-polyester-ms.txt?raw";
import d12PolyTICRaw from "@/assets/real-data/d12-polyester-tic.txt?raw";
import d12PolyMSRaw from "@/assets/real-data/d12-polyester-ms.txt?raw";
import d13PolyTICRaw from "@/assets/real-data/d13-polyester-tic.txt?raw";
import d13PolyMSRaw from "@/assets/real-data/d13-polyester-ms.txt?raw";
import d14PolyTICRaw from "@/assets/real-data/d14-polyester-tic.txt?raw";
import d14PolyMSRaw from "@/assets/real-data/d14-polyester-ms.txt?raw";
import d15PolyTICRaw from "@/assets/real-data/d15-polyester-tic.txt?raw";
import d15PolyMSRaw from "@/assets/real-data/d15-polyester-ms.txt?raw";

// Linen D16~D20 (TIC+MS: txt tab, D19 MS 결측)
import d16LinenTICRaw from "@/assets/real-data/d16-linen-tic.txt?raw";
import d16LinenMSRaw from "@/assets/real-data/d16-linen-ms.txt?raw";
import d17LinenTICRaw from "@/assets/real-data/d17-linen-tic.txt?raw";
import d17LinenMSRaw from "@/assets/real-data/d17-linen-ms.txt?raw";
import d18LinenTICRaw from "@/assets/real-data/d18-linen-tic.txt?raw";
import d18LinenMSRaw from "@/assets/real-data/d18-linen-ms.txt?raw";
import d19LinenTICRaw from "@/assets/real-data/d19-linen-tic.txt?raw";
// D19 MS 결측 — 파일 없음, msRaw = null
import d20LinenTICRaw from "@/assets/real-data/d20-linen-tic.txt?raw";
import d20LinenMSRaw from "@/assets/real-data/d20-linen-ms.txt?raw";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SampleFabric = "cotton" | "nylon" | "polyester" | "linen";

export type SampleSourceId =
  | "cotton-d1"
  | "cotton-d2"
  | "cotton-d3"
  | "cotton-d4"
  | "cotton-d5"
  | "nylon-d6"
  | "nylon-d7"
  | "nylon-d8"
  | "nylon-d9"
  | "nylon-d10"
  | "polyester-d11"
  | "polyester-d12"
  | "polyester-d13"
  | "polyester-d14"
  | "polyester-d15"
  | "linen-d16"
  | "linen-d17"
  | "linen-d18"
  | "linen-d19"
  | "linen-d20";

export const SAMPLE_SOURCE_IDS: SampleSourceId[] = [
  "cotton-d1",
  "cotton-d2",
  "cotton-d3",
  "cotton-d4",
  "cotton-d5",
  "nylon-d6",
  "nylon-d7",
  "nylon-d8",
  "nylon-d9",
  "nylon-d10",
  "polyester-d11",
  "polyester-d12",
  "polyester-d13",
  "polyester-d14",
  "polyester-d15",
  "linen-d16",
  "linen-d17",
  "linen-d18",
  "linen-d19",
  "linen-d20",
];

export interface SampleEntry {
  id: SampleSourceId;
  /** 사용자 표시 라벨 (한국어 포함) */
  label: string;
  fabric: SampleFabric;
  fabricKo: string;
  /** Vite ?raw 원본 문자열 (TIC) */
  ticRaw: string;
  /** Vite ?raw 원본 문자열 (MS). null = 결측 (D19) */
  msRaw: string | null;
  /** 장비 vendor 파일명 (데이터 헤더에서 확인) */
  vendorSource: string;
  /** MS 결측 여부 */
  msAvailable: boolean;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * D1, D6: 기존 parseD1Cotton / parseD6Nylon 모듈의 상수를 재사용한다.
 * 두 파일은 이미 각자의 ?raw import를 갖고 있으므로
 * sampleRegistry에서 ?raw를 중복 import하지 않는다.
 * ticRaw / msRaw 필드에 sentinel 문자열을 두고, getTICDataById / getMSScansById가
 * sentinel 감지 시 legacy 상수로 위임한다.
 */
const LEGACY_D1_MARKER = "__legacy_d1__";
const LEGACY_D6_MARKER = "__legacy_d6__";

export const SAMPLE_REGISTRY: Record<SampleSourceId, SampleEntry> = {
  "cotton-d1": {
    id: "cotton-d1",
    label: "Cotton D1 — 면 (diesel)",
    fabric: "cotton",
    fabricKo: "면",
    ticRaw: LEGACY_D1_MARKER,
    msRaw: LEGACY_D1_MARKER,
    vendorSource: "diesel_gas_1.D",
    msAvailable: true,
  },
  "cotton-d2": {
    id: "cotton-d2",
    label: "Cotton D2 — 면 (diesel)",
    fabric: "cotton",
    fabricKo: "면",
    ticRaw: d2CottonTICRaw,
    msRaw: d2CottonMSRaw,
    vendorSource: "diesel_gas_2.D",
    msAvailable: true,
  },
  "cotton-d3": {
    id: "cotton-d3",
    label: "Cotton D3 — 면 (diesel)",
    fabric: "cotton",
    fabricKo: "면",
    ticRaw: d3CottonTICRaw,
    msRaw: d3CottonMSRaw,
    vendorSource: "diesel_gas_3.D",
    msAvailable: true,
  },
  "cotton-d4": {
    id: "cotton-d4",
    label: "Cotton D4 — 면 (diesel)",
    fabric: "cotton",
    fabricKo: "면",
    ticRaw: d4CottonTICRaw,
    msRaw: d4CottonMSRaw,
    vendorSource: "diesel_gas_4.D",
    msAvailable: true,
  },
  "cotton-d5": {
    id: "cotton-d5",
    label: "Cotton D5 — 면 (diesel)",
    fabric: "cotton",
    fabricKo: "면",
    ticRaw: d5CottonTICRaw,
    msRaw: d5CottonMSRaw,
    vendorSource: "diesel_gas_5.D",
    msAvailable: true,
  },
  "nylon-d6": {
    id: "nylon-d6",
    label: "Nylon D6 — 나일론 (diesel)",
    fabric: "nylon",
    fabricKo: "나일론",
    ticRaw: LEGACY_D6_MARKER,
    msRaw: LEGACY_D6_MARKER,
    vendorSource: "Diesel_nilon1.D",
    msAvailable: true,
  },
  "nylon-d7": {
    id: "nylon-d7",
    label: "Nylon D7 — 나일론 (diesel)",
    fabric: "nylon",
    fabricKo: "나일론",
    ticRaw: d7NylonTICRaw,
    msRaw: d7NylonMSRaw,
    vendorSource: "Diesel_nilon2.D",
    msAvailable: true,
  },
  "nylon-d8": {
    id: "nylon-d8",
    label: "Nylon D8 — 나일론 (diesel)",
    fabric: "nylon",
    fabricKo: "나일론",
    ticRaw: d8NylonTICRaw,
    msRaw: d8NylonMSRaw,
    vendorSource: "Diesel_nilon3.D",
    msAvailable: true,
  },
  "nylon-d9": {
    id: "nylon-d9",
    label: "Nylon D9 — 나일론 (diesel)",
    fabric: "nylon",
    fabricKo: "나일론",
    ticRaw: d9NylonTICRaw,
    msRaw: d9NylonMSRaw,
    vendorSource: "Diesel_nilon4.D",
    msAvailable: true,
  },
  "nylon-d10": {
    id: "nylon-d10",
    label: "Nylon D10 — 나일론 (diesel)",
    fabric: "nylon",
    fabricKo: "나일론",
    ticRaw: d10NylonTICRaw,
    msRaw: d10NylonMSRaw,
    vendorSource: "Diesel_nilon5.D",
    msAvailable: true,
  },
  "polyester-d11": {
    id: "polyester-d11",
    label: "Polyester D11 — 폴리에스터 (diesel)",
    fabric: "polyester",
    fabricKo: "폴리에스터",
    ticRaw: d11PolyTICRaw,
    msRaw: d11PolyMSRaw,
    vendorSource: "diesel poly1.D",
    msAvailable: true,
  },
  "polyester-d12": {
    id: "polyester-d12",
    label: "Polyester D12 — 폴리에스터 (diesel)",
    fabric: "polyester",
    fabricKo: "폴리에스터",
    ticRaw: d12PolyTICRaw,
    msRaw: d12PolyMSRaw,
    vendorSource: "diesel poly2.D",
    msAvailable: true,
  },
  "polyester-d13": {
    id: "polyester-d13",
    label: "Polyester D13 — 폴리에스터 (diesel)",
    fabric: "polyester",
    fabricKo: "폴리에스터",
    ticRaw: d13PolyTICRaw,
    msRaw: d13PolyMSRaw,
    vendorSource: "diesel poly3.D",
    msAvailable: true,
  },
  "polyester-d14": {
    id: "polyester-d14",
    label: "Polyester D14 — 폴리에스터 (diesel)",
    fabric: "polyester",
    fabricKo: "폴리에스터",
    ticRaw: d14PolyTICRaw,
    msRaw: d14PolyMSRaw,
    vendorSource: "diesel poly4.D",
    msAvailable: true,
  },
  "polyester-d15": {
    id: "polyester-d15",
    label: "Polyester D15 — 폴리에스터 (diesel)",
    fabric: "polyester",
    fabricKo: "폴리에스터",
    ticRaw: d15PolyTICRaw,
    msRaw: d15PolyMSRaw,
    vendorSource: "diesel poly5.D",
    msAvailable: true,
  },
  "linen-d16": {
    id: "linen-d16",
    label: "Linen D16 — 린넨 (diesel)",
    fabric: "linen",
    fabricKo: "린넨",
    ticRaw: d16LinenTICRaw,
    msRaw: d16LinenMSRaw,
    vendorSource: "diesel linen1.D",
    msAvailable: true,
  },
  "linen-d17": {
    id: "linen-d17",
    label: "Linen D17 — 린넨 (diesel)",
    fabric: "linen",
    fabricKo: "린넨",
    ticRaw: d17LinenTICRaw,
    msRaw: d17LinenMSRaw,
    vendorSource: "diesel linen2.D",
    msAvailable: true,
  },
  "linen-d18": {
    id: "linen-d18",
    label: "Linen D18 — 린넨 (diesel)",
    fabric: "linen",
    fabricKo: "린넨",
    ticRaw: d18LinenTICRaw,
    msRaw: d18LinenMSRaw,
    vendorSource: "diesel linen3.D",
    msAvailable: true,
  },
  "linen-d19": {
    id: "linen-d19",
    label: "Linen D19 — 린넨 (TIC only)",
    fabric: "linen",
    fabricKo: "린넨",
    ticRaw: d19LinenTICRaw,
    msRaw: null,
    vendorSource: "diesel linen4.D",
    msAvailable: false,
  },
  "linen-d20": {
    id: "linen-d20",
    label: "Linen D20 — 린넨 (diesel)",
    fabric: "linen",
    fabricKo: "린넨",
    ticRaw: d20LinenTICRaw,
    msRaw: d20LinenMSRaw,
    vendorSource: "diesel linen5.D",
    msAvailable: true,
  },
};

// ─── Lazy parse cache ─────────────────────────────────────────────────────────

const ticCache = new Map<SampleSourceId, TICData[]>();
const msCache = new Map<SampleSourceId, MSScan[]>();

/**
 * ID로 TIC 데이터를 반환한다.
 * D1: parseD1Cotton.d1CottonTIC 상수 (이미 파싱됨)
 * D6: parseD6Nylon.d6NylonTIC 상수 (이미 파싱됨)
 * 나머지: parseTICCsv lazy parse + cache
 */
export function getTICDataById(id: SampleSourceId): TICData[] {
  if (id === "cotton-d1") return d1CottonTIC;
  if (id === "nylon-d6") return d6NylonTIC;

  const cached = ticCache.get(id);
  if (cached) return cached;

  const parsed = parseTICCsv(SAMPLE_REGISTRY[id].ticRaw);
  ticCache.set(id, parsed);
  return parsed;
}

/**
 * ID로 MS 스캔 배열을 반환한다.
 * D1: parseD1Cotton.d1CottonMSScans 상수
 * D6: parseD6Nylon.d6NylonMSScans 상수
 * D19: msRaw = null → 빈 배열 반환 (결측 처리)
 */
export function getMSScansById(id: SampleSourceId): MSScan[] {
  if (id === "cotton-d1") return d1CottonMSScans as MSScan[];
  if (id === "nylon-d6") return d6NylonMSScans as MSScan[];

  const cached = msCache.get(id);
  if (cached) return cached;

  const raw = SAMPLE_REGISTRY[id].msRaw;
  const parsed = raw ? parseMSScans(raw) : [];
  msCache.set(id, parsed);
  return parsed;
}

/**
 * ID + RT로 nearest MS scan 데이터를 반환한다.
 * D19 (MS 결측) 또는 스캔 없음 → empty array
 * 결과는 mz 오름차순 정렬
 */
export function getMassSpectrumByRT(
  id: SampleSourceId,
  rt: number,
): MassSpectrumData[] {
  const scans = getMSScansById(id);
  const nearest = getNearestScan(scans, rt);
  if (!nearest || nearest.data.length === 0) return [];
  return [...nearest.data].sort((a, b) => a.mz - b.mz);
}

/**
 * Sprint 22 #14: sample의 MS scan들을 mz×RT 평면 데이터로 펼침.
 * 각 scan의 모든 (mz, intensity) 포인트를 그 scan의 RT와 함께 (rt, mz, intensity) 튜플로.
 * MZRTPlot의 입력 형식과 일치. featureId는 *없음* (sample 데이터는 cotton-d1 features 매핑과 무관).
 */
export interface MZRTPoint {
  rt: number;
  mz: number;
  intensity: number;
  feature_id: string; // empty fallback "" — MZRTPlot Cell color는 selectedFeature와 mz 매칭만
}

const mzrtCache = new Map<SampleSourceId, MZRTPoint[]>();

export function getMZRTDataById(id: SampleSourceId): MZRTPoint[] {
  const cached = mzrtCache.get(id);
  if (cached) return cached;

  const scans = getMSScansById(id);
  const points: MZRTPoint[] = [];
  for (const scan of scans) {
    for (const point of scan.data) {
      points.push({
        rt: scan.rt,
        mz: point.mz,
        intensity: point.intensity,
        feature_id: "", // sample 직접 측정 — feature mapping 없음
      });
    }
  }
  mzrtCache.set(id, points);
  return points;
}

/**
 * Sprint 22 #14: TIC 클릭 시 sample의 MS scan RT 중 nearest 반환.
 * features (cotton 12개 RT)와 무관 — 현재 sample의 실측 scan RT로 snap.
 */
export function getNearestScanRT(id: SampleSourceId, rt: number): number | null {
  const scans = getMSScansById(id);
  if (scans.length === 0) return null;
  const nearest = scans.reduce((prev, cur) =>
    Math.abs(cur.rt - rt) < Math.abs(prev.rt - rt) ? cur : prev,
  );
  return nearest.rt;
}

// ─── Fabric-grouped view (selector UI용) ─────────────────────────────────────

export interface FabricGroup {
  fabric: SampleFabric;
  fabricKo: string;
  samples: SampleEntry[];
}

export const FABRIC_GROUPS: FabricGroup[] = [
  { fabric: "cotton", fabricKo: "면", samples: [] },
  { fabric: "nylon", fabricKo: "나일론", samples: [] },
  { fabric: "polyester", fabricKo: "폴리에스터", samples: [] },
  { fabric: "linen", fabricKo: "린넨", samples: [] },
];

// 레지스트리에서 그룹을 채운다 (SAMPLE_SOURCE_IDS 순서 유지)
for (const id of SAMPLE_SOURCE_IDS) {
  const entry = SAMPLE_REGISTRY[id];
  const group = FABRIC_GROUPS.find((g) => g.fabric === entry.fabric);
  if (group) group.samples.push(entry);
}
